import { forwardRef, useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import composeRefs from '@seznam/compose-react-refs';

import InfoIcon from 'shared/assets/images/icons/info-small.svg';
import StepperDownSVG from 'shared/assets/images/icons/stepper-down.svg';
import StepperUpSVG from 'shared/assets/images/icons/stepper-up.svg';
import { Tooltip } from '../Tooltip/Tooltip';
import { Typography } from '../Typography/Typography';

import './NumberStepper.css';

export interface NumberStepperProps extends React.InputHTMLAttributes<HTMLInputElement> {
	name: string;
	id?: string;
	step?: number;
	min?: number;
	max?: number;
	label?: string;
	tooltipMessage?: string;
	disabled?: boolean;
}

const { Text } = Typography;

export const NumberStepper = forwardRef<HTMLInputElement, NumberStepperProps>(
	({ name, id, step = 1, min, max, label, tooltipMessage, disabled }, ref): JSX.Element => {
		const { control } = useFormContext();
		const internalFieldRef = useRef<HTMLInputElement>(null);

		return (
			<div className='number-stepper__wrapper'>
				<label htmlFor={id} className='number-stepper__label'>
					{label && (
						<div className='input__label'>
							<Text variant='note'>{label}</Text>
							{tooltipMessage && (
								<Tooltip
									message={tooltipMessage}
									className='input__tooltip'
									messageWidth='15rem'
									size='small'
									position={[`middle`, `left-side`]}
								>
									<InfoIcon />
								</Tooltip>
							)}
						</div>
					)}
				</label>
				<div className='number-stepper-box'>
					<Controller
						name={name}
						control={control}
						render={({ field }) => {
							const isMinimalValue = min !== undefined && Number(field.value) <= min;
							const isMaximalValue = max !== undefined && Number(field.value) >= max;

							const handleValueChange = (newValue: number): void => {
								if ((min !== undefined && newValue < min) || (max !== undefined && newValue > max)) {
									return;
								}
								field.onChange(newValue);
							};

							return (
								<>
									<input
										className='number-stepper__field'
										id={id}
										type='number'
										// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
										value={Number.isNaN(field.value) ? `` : field.value}
										step={step}
										min={min}
										max={max}
										disabled={disabled}
										onChange={e => handleValueChange(Number(e.target.value))}
										onBlur={field.onBlur}
										// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
										ref={composeRefs(internalFieldRef, ref)}
									/>
									<div className='number-stepper__buttons'>
										<button
											className='number-stepper__button'
											type='button'
											disabled={disabled || isMaximalValue}
											onClick={() => handleValueChange(Number(field.value || 0) + step)}
										>
											<StepperUpSVG />
										</button>
										<button
											className='number-stepper__button'
											type='button'
											disabled={disabled || isMinimalValue}
											onClick={() => handleValueChange(Number(field.value || 0) - step)}
										>
											<StepperDownSVG />
										</button>
									</div>
								</>
							);
						}}
					/>
				</div>
			</div>
		);
	},
);
